import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { media, addEventListener, removeEventListener, scrollTop } from '~/utils';

import MailIcon from '@public/icons/mail.svg';

type Props = {
  /**
   * color: buttonの背景色を決定する。初期値は#000。
   */
  color?: string;
  /**
   * text: 初期値は「お問い合せ」。
   */
  text?: string;
  /**
   * link: buttonを押した遷移先。別タブにて開く。初期値はお問い合わせフォームへ移動する。
   */
  link?: string;
};

export function ContactButton({
  color = '#000',
  text = 'お問い合せ',
  link = 'https://forms.gle/2fGWMoY2uosWp6Ht8',
}: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  useEffect(() => {
    addEventListener('scroll', handleScroll);
    return () => {
      removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScroll = () => {
    const st = scrollTop();

    setIsOpen(st < lastScrollTop);
    setLastScrollTop(st);
  };

  return (
    <a href={link} target='_blank' rel='noopener noreferrer'>
      <Wrapper color={color} className={isOpen ? '' : 'close'}>
        <SvgWrapper>
          <MailIcon />
        </SvgWrapper>
        <Text>{text}</Text>
      </Wrapper>
    </a>
  );
}

const Wrapper = styled.div<{ color?: string }>`
  width: 160px;
  height: 160px;
  background-color: ${(props) => (props.color ? props.color : '#000')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);

  svg {
    stroke: #fff;
    fill: ${(props) => (props.color ? props.color : '#000')};
    transition: 0.5s;
  }

  &:hover {
    background-color: #fff;

    svg {
      stroke: ${(props) => (props.color ? props.color : '#000')};
      fill: #fff;
      transform: rotate(-25deg);
    }

    > div:nth-child(2) {
      color: ${(props) => (props.color ? props.color : '#000')};
    }
  }

  ${media.sp`
    border-radius: 40px;
    width: calc(100% - 40px);
    height: 80px;
    flex-direction: row;
    justify-content: flex-start;
    transition: 0.5s;
    position: absolute;
    bottom: 0;
    right: 0;

    svg {
      width: 44px;
      transform: rotate(-25deg)!important;
    }

    > div:nth-child(2) {
      transition: 0.7s;
      visibility: visible;
      opacity: 1;
    }

    &.close {
      width: 80px;

      svg {
        transform: rotate(0)!important;
      }

      > div:nth-child(2) {
        transition: 0.2s;
        visibility: hidden;
        width: 0;
        opacity: 0;
      }
    }

    &:hover {
      background-color: ${(props: { color?: boolean }) => (props.color ? props.color : '#000')};

      svg {
        stroke: #fff;
        fill: ${(props: { color?: boolean }) => (props.color ? props.color : '#000')};
        transform: rotate(0);
      }

      > div:nth-child(2) {
        color: #fff;
      }
    }
  `}
`;

const SvgWrapper = styled.div`
  ${media.sp`
    width: 80px;
    height: 80px;
    border-radius: 40px;
    border: 1px solid #fff;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  `}
`;

const Text = styled.div`
  margin-top: 10px;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  white-space: pre-wrap;
  line-height: 25px;

  ${media.sp`
    margin-top: 0;
    font-size: 18px;
    flex: 1;
    text-align: center;
    letter-spacing: 3px;
  `}
`;
